import { useEffect, useRef, useState } from "react";
import { siteConfig } from "@/lib/site-config";

interface FormEmbedProps {
  id?: string;
  className?: string;
  /** Load immediately instead of waiting for the viewport (e.g. inside a modal the user just opened). */
  eager?: boolean;
}

/**
 * Embeds the LeadConnector form widget exactly as provided in the business widget code.
 * We never force a height, scroll, or compression — form_embed.js controls iframe height.
 *
 * The iframe + form_embed.js pull in heavy third parties (reCAPTCHA, etc.), so on the
 * homepage — where the form sits below the fold on mobile — we defer loading until it
 * approaches the viewport. A same-height placeholder holds the space to avoid layout shift.
 * Modals pass `eager` so the form is ready the instant they open.
 */
const FormEmbed = ({ id = "inline-GObn6QwrDwagth8Bo2xl", className = "", eager = false }: FormEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(eager);

  // Reveal (and load) the form once it nears the viewport — unless eager.
  useEffect(() => {
    if (inView) return;
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      // Require the form to be meaningfully inside the viewport before loading. This keeps it
      // eager on desktop (where it sits beside the headline) while deferring it on mobile
      // (where it sits below the fold) until the visitor scrolls to it.
      { rootMargin: "0px 0px -100px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // Inject form_embed.js only when the form is about to render.
  useEffect(() => {
    if (!inView) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    } else {
      // Script already loaded — re-run any global init if available.
      // form_embed.js auto-attaches via postMessage, so remounting the iframe is enough.
      try {
        // @ts-expect-error - global form embed init
        if (typeof window.formEmbedInit === "function") window.formEmbedInit();
      } catch {
        // noop
      }
    }
  }, [inView]);

  return (
    <div ref={containerRef} className={className}>
      {inView ? (
        <iframe
          src={siteConfig.widgets.formIframeSrc}
          style={{ width: "100%", border: "none", borderRadius: 10, display: "block", minHeight: 640 }}
          id={id}
          data-layout="{'id':'INLINE'}"
          data-trigger-type="alwaysShow"
          data-trigger-value=""
          data-activation-type="alwaysActivated"
          data-activation-value=""
          data-deactivation-type="neverDeactivate"
          data-deactivation-value=""
          data-form-name={siteConfig.widgets.formName}
          data-height="729"
          data-layout-iframe-id={id}
          data-form-id={siteConfig.widgets.formId}
          title={siteConfig.widgets.formName}
          scrolling="no"
        />
      ) : (
        <div
          className="w-full rounded-[10px] bg-muted/30 animate-pulse"
          style={{ minHeight: 640 }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default FormEmbed;
